import type { AnalyticsAdapter, UserVars } from "./analyticsAdapter"
import { EventBase } from "../event/eventBase"
import { LogLevel } from "../logger"
import {
  mapAnalyticsDomainToInteractionName,
  type Domain,
} from "../event/domain"

interface FSPageClassType {
  start(): void

  update(properties: Record<string, unknown>): void

  end(): void
}

type FSPage = new (
  pageName: string,
  properties?: Record<string, unknown>
) => FSPageClassType

export enum FullStoryLogLevel {
  Log = 0,
  Debug = 1,
  Info = 2,
  Warn = 3,
  Error = 4,
  Assert = 5,
}

export interface FullStoryAPI {
  anonymize(): void

  event(eventName: string, eventProperties: Record<string, unknown>): void

  identify(uid: string, userVars?: UserVars): void

  log(logLevel: number, message: string): void
}

interface AdapterOpts {
  FSPage: FSPage
}

export class FullStoryAdapter implements AnalyticsAdapter {
  #currentPage: FSPageClassType | undefined = void 0

  public constructor(
    private readonly api: FullStoryAPI,
    private readonly opts: AdapterOpts
  ) {}

  public emit<TDomain extends Domain, TEvent extends string>(
    ...events: EventBase<TDomain, TEvent>[]
  ): void {
    for (const evt of events) {
      const eventType = mapAnalyticsDomainToInteractionName(evt.domain)
      this.api.event(eventType, {
        domain: evt.domain,
        ...evt.context,
        eventSubType: evt.eventName,
      })
    }
  }

  public viewPage(pageName: string, params?: Record<string, unknown>): void {
    this.#currentPage = new this.opts.FSPage(pageName, params)
    try {
      this.#currentPage.start()
    } catch {
      // do nothing
    }
  }

  public updatePage(params?: Record<string, unknown>): void {
    if (this.#currentPage) {
      this.#currentPage.update(params ?? {})
    }
  }

  public unloadPage(): void {
    this.#currentPage?.end()
  }

  public log(level: EnumValues<typeof LogLevel>, message: string): void {
    this.api.log(this.toFullStoryLogLevel(level), message)
  }

  public anonymize(): Promise<void> {
    this.api.anonymize()
    return Promise.resolve()
  }

  public identify(userVars: UserVars): Promise<void> {
    this.api.identify(userVars.id, userVars)
    return Promise.resolve()
  }

  private toFullStoryLogLevel = (
    level: EnumValues<typeof LogLevel>
  ): FullStoryLogLevel => {
    switch (level) {
      case LogLevel.DEFAULT: {
        return FullStoryLogLevel.Log
      }
      case LogLevel.DEBUG: {
        return FullStoryLogLevel.Debug
      }
      case LogLevel.INFO: {
        return FullStoryLogLevel.Info
      }
      case LogLevel.WARN: {
        return FullStoryLogLevel.Warn
      }
      case LogLevel.ERROR: {
        return FullStoryLogLevel.Error
      }
      case LogLevel.FAIL: {
        return FullStoryLogLevel.Assert
      }
      default: {
        return FullStoryLogLevel.Log
      }
    }
  }

  public addSessionContext(_newContext: Record<string, unknown>): void {
    // No-op
  }
}
